"use client";

import React from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface CustomSelectProps {
    label: string;
    value: string | null;
    onChange: (value: string | null) => void;
    options: Array<{ id: string; name: string }>;
    disabled?: boolean;
    onClear?: () => void;
    showClear?: boolean;
}

export default function CustomSelect({
    label,
    value,
    onChange,
    options,
    disabled = false,
    onClear,
    showClear = false
}: CustomSelectProps) {
    return (
        <div>
            {/* Label above the dropdown */}
            <label className="block text-sm font-medium text-gray-600 mb-1">
                {label}
            </label>

            <div className="relative">
                <select
                    className={`
                        w-full px-3 py-3 pr-10
                        bg-white border border-gray-300 rounded-lg
                        text-gray-700 text-sm
                        focus:outline-none focus:border-[#0055D4] focus:ring-1 focus:ring-[#0055D4]
                        transition-all duration-200
                        appearance-none cursor-pointer
                        ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
                    `}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value || null)}
                    disabled={disabled}
                >
                    <option value="" className="text-gray-700">All</option>
                    {options?.length > 0 && options?.map((option) => (
                        <option key={option.id} value={option.id} className="text-gray-700">
                            {option.name}
                        </option>
                    ))}
                </select>

                {/* Custom dropdown arrow */}
                <ChevronDownIcon
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                />

                {/* Clear button */}
                {showClear && value && onClear && (
                    <button
                        onClick={onClear}
                        className="absolute -top-2 -right-2 bg-gray-100 hover:bg-gray-200 rounded-full p-1 transition-colors z-10"
                        type="button"
                    >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}